import { useCallback } from 'react';

import { ActionKey, ActionSubjectIdKey, ActionSubjectKey } from '@/analytics/constants';
import { useFastshiftAnalyticsData } from '@/analytics/hooks/use-fastshift-analytics-data';
import { useFireAnalyticsEvents } from '@/analytics/use-fire-analytics-events';

import type {
	FastshiftCsProfilerCustomerProfile,
	FastshiftCsProfilerCustomerProfileTheme,
	FastshiftCsProfilerLinkedTickets,
	FastshiftCsProfilerProfilingOptions,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import type { CsProfilerCustomerProfile, CsProfilerLinkedTicketsState } from '@/state/types';

type ProfilerSelection = {
	region: string;
	industry: string;
	segment: string;
};

type ProfilingOptionsState = {
	pending: boolean;
	error: string | null;
	data: FastshiftCsProfilerProfilingOptions | null;
};

type CustomerProfileDetailsEvent = {
	customerProfile: CsProfilerCustomerProfile;
	isCustomerProfileForCurrentSelection: boolean;
	hasCustomerProfileErrorForCurrentSelection: boolean;
	currentSelection: ProfilerSelection | null;
};

type ThemeToggleEvent = {
	themeName: string;
	themeTier: FastshiftCsProfilerCustomerProfileTheme['tier'];
	isExpanded: boolean;
};

type LinkedTicketsToggleEvent = {
	themeName: string;
	isExpanded: boolean;
};

type LinkedTicketsDetailsEvent = {
	themeName: string;
	linkedTicketsState?: CsProfilerLinkedTicketsState;
};

type LinkedTicketLinkEvent = {
	themeName: string;
	ticketType: 'ent' | 'jpd';
	ticketKey: string;
};

const getSelectionAttributes = (selection: ProfilerSelection) => ({
	selectedRegion: selection.region,
	selectedIndustry: selection.industry,
	selectedSegment: selection.segment,
});

const getCustomerProfileAttributes = (customerProfile: FastshiftCsProfilerCustomerProfile) => ({
	...getSelectionAttributes(customerProfile.selection),
	totalItems: customerProfile.totalItems,
	blockerCount: customerProfile.blockerCount,
	watchCount: customerProfile.watchCount,
});

const getLinkedTicketsCounts = (linkedTickets: FastshiftCsProfilerLinkedTickets) => ({
	entTicketCount: linkedTickets.entTickets.length,
	jpdTicketCount: linkedTickets.jpdTickets.length,
});

export const useFastshiftCsProfilerAnalytics = () => {
	const { sourceKey, identityAttrs, uiIdentityAttrs } = useFastshiftAnalyticsData();
	const { fireScreenEvent, fireTrackEvent } = useFireAnalyticsEvents(identityAttrs);
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);

	const sendCsProfilerScreenEvent = useCallback(() => {
		if (identityAttrs === null) {
			return false;
		}

		fireScreenEvent({ name: sourceKey });
		return true;
	}, [fireScreenEvent, identityAttrs, sourceKey]);

	const sendProfilingOptionsDetailsTrackEvent = useCallback(
		({ pending, error, data }: ProfilingOptionsState) => {
			if (identityAttrs === null || pending || (!error && data === null)) {
				return false;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.SECTION,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_FILTERS_SECTION_DETAILS,
				source: sourceKey,
				attributes: error
					? { status: 'error' }
					: {
							status: 'normal',
							regionOptionCount: data?.regions.length ?? 0,
							industryOptionCount: data?.industries.length ?? 0,
							segmentOptionCount: data?.segments.length ?? 0,
						},
			});
			return true;
		},
		[fireTrackEvent, identityAttrs, sourceKey],
	);

	const sendCustomerProfileDetailsTrackEvent = useCallback(
		({
			customerProfile,
			isCustomerProfileForCurrentSelection,
			hasCustomerProfileErrorForCurrentSelection,
			currentSelection,
		}: CustomerProfileDetailsEvent) => {
			if (identityAttrs === null || customerProfile.pending) {
				return false;
			}

			if (hasCustomerProfileErrorForCurrentSelection) {
				fireTrackEvent({
					action: ActionKey.DISPLAYED,
					actionSubject: ActionSubjectKey.SECTION,
					actionSubjectId: ActionSubjectIdKey.CS_PROFILER_RESULTS_SECTION_DETAILS,
					source: sourceKey,
					attributes: {
						status: 'error',
						...(currentSelection ? getSelectionAttributes(currentSelection) : {}),
					},
				});
				return true;
			}

			if (!isCustomerProfileForCurrentSelection || !customerProfile.data) {
				return false;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.SECTION,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_RESULTS_SECTION_DETAILS,
				source: sourceKey,
				attributes: {
					status: customerProfile.data.themes.length ? 'normal' : 'empty',
					...getCustomerProfileAttributes(customerProfile.data),
				},
			});
			return true;
		},
		[fireTrackEvent, identityAttrs, sourceKey],
	);

	const sendProfileCustomerButtonClickUIEvent = useCallback(
		(selection: ProfilerSelection) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_PROFILE_CUSTOMER_BUTTON,
				source: sourceKey,
				attributes: getSelectionAttributes(selection),
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendExportToConfluenceButtonClickUIEvent = useCallback(
		(customerProfile: FastshiftCsProfilerCustomerProfile) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_EXPORT_TO_CONFLUENCE_BUTTON,
				source: sourceKey,
				attributes: getCustomerProfileAttributes(customerProfile),
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendThemeRowToggleClickUIEvent = useCallback(
		({ themeName, themeTier, isExpanded }: ThemeToggleEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_THEME_ROW_TOGGLE,
				source: sourceKey,
				attributes: { themeName, themeTier, isExpanded },
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendLinkedTicketsToggleClickUIEvent = useCallback(
		({ themeName, isExpanded }: LinkedTicketsToggleEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_LINKED_TICKETS_TOGGLE,
				source: sourceKey,
				attributes: { themeName, isExpanded },
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendLinkedTicketsDetailsTrackEvent = useCallback(
		({ themeName, linkedTicketsState }: LinkedTicketsDetailsEvent) => {
			const linkedTickets = linkedTicketsState?.data;

			if (
				identityAttrs === null ||
				!linkedTicketsState ||
				(linkedTicketsState.pending && !linkedTickets) ||
				(!linkedTicketsState.error && !linkedTickets)
			) {
				return false;
			}

			const ticketCounts = linkedTickets ? getLinkedTicketsCounts(linkedTickets) : null;
			const status = linkedTicketsState.error
				? 'error'
				: ticketCounts && (ticketCounts.entTicketCount > 0 || ticketCounts.jpdTicketCount > 0)
					? 'normal'
					: 'empty';

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.SECTION,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_LINKED_TICKETS_DETAILS,
				source: sourceKey,
				attributes: {
					themeName,
					status,
					...(ticketCounts ?? {}),
				},
			});
			return true;
		},
		[fireTrackEvent, identityAttrs, sourceKey],
	);

	const sendLinkedTicketLinkClickUIEvent = useCallback(
		({ themeName, ticketType, ticketKey }: LinkedTicketLinkEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.CS_PROFILER_LINKED_TICKET_LINK,
				source: sourceKey,
				attributes: { themeName, ticketType, ticketKey },
			});
		},
		[fireUIEvent, sourceKey],
	);

	return {
		sendCsProfilerScreenEvent,
		sendProfilingOptionsDetailsTrackEvent,
		sendCustomerProfileDetailsTrackEvent,
		sendProfileCustomerButtonClickUIEvent,
		sendExportToConfluenceButtonClickUIEvent,
		sendThemeRowToggleClickUIEvent,
		sendLinkedTicketsToggleClickUIEvent,
		sendLinkedTicketsDetailsTrackEvent,
		sendLinkedTicketLinkClickUIEvent,
	};
};
